//****************************************
// Plot Legend Prototype
// DOM Registration: No
// National Instruments Copyright 2015
//****************************************

/**
## ni-plot-legend

> A custom element that provides a plot legend.

Use it to visualize and change the plots of a graph or chart element.

It provides an expandable list with all the plots of the graphs.

Expand one of the plots and you get a panel where you can control the
parameters of the plot - like plot style, line width, dash style, colors ...

Example:
```html
<ni-cartesian-graph graph-ref="g1" value="[[2, 3, 5, 7, 11, 13, 17, 19],
                          [0, 1, 1, 2, 3, 5, 8, 13]]">
    <ni-cartesian-axis show label="Index" axis-position="bottom"></ni-cartesian-axis>
    <ni-cartesian-axis show label="Value" axis-position="left"></ni-cartesian-axis>
    <ni-cartesian-plot show label="Prime Numbers">
        <ni-cartesian-plot-renderer line-width="2" line-stroke="red">
        </ni-cartesian-plot-renderer>
    </ni-cartesian-plot>
    <ni-cartesian-plot show label="Fibonacci Numbers">
        <ni-cartesian-plot-renderer line-width="2" line-stroke="blue">
        </ni-cartesian-plot-renderer>
    </ni-cartesian-plot>
</ni-cartesian-graph>

<ni-plot-legend graph-ref="g1" mode="pan"></ni-plot-legend>
```
*/

(function() {
    'use strict';

    var NI_SUPPORT = NationalInstruments.HtmlVI.NISupport;
    var $ = NationalInstruments.Globals.jQuery;

    // create a view for a new plot at the end of the legend.
    var createLegendRow = function(legend) {
        legend.createRow(legend.expanderWrapper, legend.nPlots);
        legend.helpers.addRenderer(legend.nPlots);
        legend.nPlots++;
    };

    // remove the view for the plot at the specified index
    var removeLegendRow = function(legend, index) {
        if (legend.nPlots > 0) {
            legend.removeRow(legend.expanderWrapper, index);
            legend.helpers.clearRenderer(index);
            legend.nPlots--;
        }
    };

    class PlotLegend extends NationalInstruments.HtmlVI.Elements.GraphLegend {
        /**### ni-plot-legend properties*/

        /// include ./ni-graph-legend.js
        static get properties() {
            const props = Object.assign({}, super.properties, {
                /** **mode** (string) - the mode of the plot legend. Default: 'full'.
                When in 'compact mode' only the plot icon and the plot name are shown.
                In full mode you can expand the plots and can fully configure them at runtime.

                Accepted values: 'full', 'compact' */
                'mode': {
                    value: 'full',
                    type: 'string'
                },

                /** **tooltips** - whether or not to display plot name tooltips */
                'tooltips': {
                    value: false,
                    type: 'boolean'
                }
            });

            return props;
        }

        created() {
            super.created();

            this.expanderWrapper = undefined;
            this.nPlots = 0;
            this.helpers = new NationalInstruments.HtmlVI.Elements.PlotLegendHelpers();
            this.plotLegendItemDisplay = new NationalInstruments.HtmlVI.Elements.PlotLegendItemDisplay(this.helpers);
            this.uihelpers = new NationalInstruments.HtmlVI.Elements.LegendHelpers(this.helpers, this.plotLegendItemDisplay);
            this.deferLegendUpdates = false;
        };

        // respond to a plot add (unless the updates are defered)
        plotAdded() {
            if (!this.deferLegendUpdates) {
                createLegendRow(this);
            }
        };

        // respond to a plot remove (unless the updates are defered)
        plotRemoved(index) {
            if (!this.deferLegendUpdates) {
                removeLegendRow(this, index);
            }
        };

        // call syncPlotLegendWithGraph from time to time, when the updates are defered, to
        // synchronize the legend with the current state of the plots in the graph.
        syncPlotLegendWithGraph() {
            if (this.deferLegendUpdates) {
                var graph = this.findGraph();
                if (!graph) {
                    return;
                }

                var graphPlots = graph.plots.length;

                while (graphPlots < this.nPlots) {
                    var index = this.nPlots - 1;
                    removeLegendRow(this, index);
                }

                while (graphPlots > this.nPlots) {
                    createLegendRow(this);
                }
            }
        };

        createRows() {
            var i;
            for (i = 0; i < this.nPlots; i++) {
                this.createRow(this.expanderWrapper, i);
            }
        };

        removeRows() {
            var i;
            for (i = this.nPlots - 1; i >= 0; i--) {
                this.removeRow(this.expanderWrapper, i);
            }
        };

        throttlePlotUpdates(enableThrottling) {
            var graph = this.findGraph();
            if (graph !== null) {
                graph.throttlePlotUpdates(enableThrottling);
                this.deferLegendUpdates = enableThrottling;
            }
        };

        attachToGraph(graphElement) {
            if (this.helpers.graph !== graphElement) {
                this.detachFromGraph();
            } else if (graphElement && graphElement.plots.length < this.nPlots) {
                this.removeRows();
            }

            if (graphElement && graphElement.isReady && this.helpers.graph !== graphElement) {
                this.helpers.graph = graphElement;
                this.nPlots = graphElement.plots.length;
                this.createRows();
                graphElement.registerPlotLegend(this);
            }
        };

        detachFromGraph() {
            this.helpers.clearCache();
            this.helpers.graph = null;
            this.removeRows();
            this.nPlots = 0;
        };

        attached() {
            var childElement,
                expanderWrapper,
                that = this;

            super.attached();

            this.helpers.isInEditMode = this.isInEditMode;
            childElement = document.createElement('div');
            childElement.style.width = '100%';
            childElement.style.height = '100%';
            this.appendChild(childElement);
            expanderWrapper = document.createElement('jqx-accordion');
            expanderWrapper.style.width = '100%';
            expanderWrapper.style.height = '100%';
            expanderWrapper.animation = 'none';
            if (this.mode === 'full') {
                expanderWrapper.expandMode = 'toggle';
            } else {
                expanderWrapper.expandMode = 'none';
            }
            expanderWrapper.disabled = this.isDisabledOrInEditMode();

            childElement.appendChild(expanderWrapper);
            this.expanderWrapper = expanderWrapper;

            if (this.mode === 'full') {
                expanderWrapper.addEventListener('expanding', function(event) {
                    var accordionItem = that.expanderWrapper.querySelectorAll('jqx-accordion-item')[event.detail.index];
                    var details = accordionItem.querySelector('.ni-details-box');
                    that.loadContentIfNeeded(details, event.detail.index);
                });
            }

            this.addEventListener('ni-cartesian-plot-attached', function() {
                that.plotAdded();
                that.helpers.clearCache(); // clears the renderer cache
            });

            this.addEventListener('ni-cartesian-plot-detached', function(evt) {
                var index;
                var source = evt.detail.originalSource;
                index = that.helpers.getIndex(source);

                that.plotRemoved(index);
                that.helpers.clearCache(); // clears the renderer cache
            });

            this.addEventListener('ni-cartesian-plot-changed', function(evt) {
                var index;
                var source = evt.detail.originalSource;
                index = that.helpers.getIndex(source);
                that.updatePlotName(index, that.helpers.getPlotName(index));
            });

            this.addEventListener('ni-cartesian-plot-renderer-changed', function(evt) {
                var index;
                var source = evt.detail.originalSource;
                index = that.helpers.rendererToIndex(source); // the event is routed through a plot and we gat a plot as a source
                that.plotLegendItemDisplay.updateShape(index);
            });

            this.addEventListener('ni-cartesian-plot-renderer-attached', function(evt) {
                var index;
                var source = evt.detail.originalSource;

                that.helpers.clearCache(); // clears the renderers cache
                index = that.helpers.rendererToIndex(source); // the event is routed through a plot and we got a plot as a source
                that.plotLegendItemDisplay.updateShape(index);
            });

            this.addEventListener('ni-cartesian-plot-renderer-detached', function(evt) {
                var index;
                var source = evt.detail.originalSource;

                that.helpers.clearCache(); // clears the renderers cache
                index = that.helpers.rendererToIndex(source); // the event is routed through a plot and we got a plot as a source
                that.plotLegendItemDisplay.updateShape(index);
            });

            // update the entries in plot legend based on data series length
            // The legend will apply this updates only at runtime in compact mode
            this.addEventListener('ni-plot-count-changed', function(evt) {
                var source = evt.detail.originalSource;
                if (that.mode === "compact") {
                    while (source.plotCount < that.nPlots) {
                        var index = that.nPlots - 1;
                        removeLegendRow(that, index);
                    }
                    while (source.plotCount > that.nPlots) {
                        createLegendRow(that);
                    }
                }
            });

            var graph = this.findGraph();

            if (graph !== null) {
                this.attachToGraph(graph);
            }
        };

        createRow(expanderWrapper, index) {
            var exp, header, content, div, plotName, p, span;

            exp = document.createElement('jqx-accordion-item');
            exp.animation = "none";
            exp.id = 'expander' + index;
            exp.className = 'ni-plot-legend-box';
            expanderWrapper.appendChild(exp);
            header = document.createElement('div');
            header.id = 'header' + index;
            header.className = 'ni-master-row';
            exp.querySelector('.jqx-label').appendChild(header);

            div = document.createElement('div');
            div.className = 'ni-plot-display';
            header.appendChild(div);
            this.plotLegendItemDisplay.createHeaderDisplay(div, index);
            this.plotLegendItemDisplay.updateShape(index);

            span = document.createElement('span');
            span.className = 'ni-plot-title';

            plotName = this.helpers.getPlotName(index);
            if (this.mode === 'compact') {
                if (!this.helpers.getPlot(index)) {
                    plotName = 'Plot ' + (index + 1);
                }
            }

            if (this.tooltips) {
                span.title = plotName;
            }

            p = document.createTextNode(plotName);
            span.appendChild(p);
            header.appendChild(span);

            content = document.createElement('div');
            content.id = 'content' + index;
            content.className = 'ni-details-box';
            exp.querySelector('.jqx-content-container').appendChild(content);

            if (this.mode === 'full') {
                exp.expanded = false;
            }

            this.applyFont();
        };

        updatePlotName(index, name) {
            var titles = $(this.expanderWrapper).find('.ni-plot-title');
            $(titles[index]).text(name);
        };

        loadContentIfNeeded(content, index) {
            if (content.querySelector('.ni-details') !== null) {
                return;
            }

            var tbl, tr, j, span, p, td1, td2;
            // content.style.height = '((this.helpers.menu.length + 1) * 30) + 10 + 'px'';
            tbl = document.createElement('table');
            tbl.className = 'ni-details';
            content.appendChild(tbl);

            for (j = 0; j < this.helpers.menu.length; j++) {
                var menuItem = this.helpers.menu[j];
                tr = document.createElement('tr');
                tr.className = 'ni-details-row ' + (menuItem.cssClass || '');
                tbl.appendChild(tr);
                td1 = document.createElement('td');
                td1.className = 'ni-detail-row-title-box';
                tr.appendChild(td1);
                span = document.createElement('span');
                span.className = 'ni-detail-row-title';
                td1.appendChild(span);
                var text = NI_SUPPORT.i18n(menuItem.tag);
                if (text.substring(0, 5) === '[msg_') { // in case server is not running
                    text = menuItem.name + '!';
                }

                p = document.createTextNode(text);
                span.appendChild(p);
                td2 = document.createElement('td');
                td2.className = 'ni-details-row-operations-box';
                tr.appendChild(td2);
                switch (menuItem.type) {
                    case 'buttons':
                        this.uihelpers.addButtons(menuItem, td2, index);
                        break;
                    case 'combobox':
                        this.uihelpers.addComboBox(menuItem, td2, index, 24, 'ni-selector');
                        break;
                    case 'colorbox':
                        this.uihelpers.addColorBox(menuItem, td2, index, 'ni-colorbox-selector ni-selector');
                        break;
                    case 'checkbox':
                        this.uihelpers.addCheckBox(menuItem, td2, index, 'ni-hover-box');
                        break;
                }
            }
        };

        removeRow(expanderWrapper, index) {
            this.plotLegendItemDisplay.removeHeaderDisplay(null, index);
            expanderWrapper.removeAt(index);
        };

        setFont(fontSize, fontFamily, fontWeight, fontStyle, textDecoration) {
            var childElement = this.firstElementChild,
                thisRef = $(this),
                jqrefContent = $(childElement).find(' .jqx-widget-content'),
                jqrefItemTitle = $(childElement).find(' .ni-plot-title'),
                jqrefItemContentTitle = $(childElement).find(' .ni-detail-row-title');

            thisRef.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });

            jqrefContent.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
            jqrefItemTitle.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
            jqrefItemContentTitle.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
        };

        applyFont() {
            var childElement = this.firstElementChild,
                jqrefContent = $(childElement).find(' .jqx-widget-content'),
                jqrefItemTitle = $(childElement).find(' .ni-plot-title'),
                jqrefItemContentTitle = $(childElement).find(' .ni-detail-row-title');

            var fontSize = $(this).css('font-size');
            var fontFamily = $(this).css('font-family');
            var fontWeight = $(this).css('font-weight');
            var fontStyle = $(this).css('font-style');
            jqrefContent.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
            jqrefItemTitle.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
            jqrefItemContentTitle.css({
                'font-size': fontSize,
                'font-family': fontFamily,
                'font-weight': fontWeight,
                'font-style': fontStyle
            });
        };

        propertyChangedHandler(propertyName, oldValue, newValue) {
            super.propertyChangedHandler(propertyName, oldValue, newValue);
            let that = this;

            switch (propertyName) {
                case 'graphRef':
                    // TODO: add a plot-legend-detached event for parent graph
                    this.helpers.graph.plotLegend = null;
                    var graphElement = this.findGraph();
                    this.attachToGraph(graphElement);

                    break;
                case 'disabled':
                case 'isInEditMode':
                    // this changes once after the element is created
                    this.helpers.isInEditMode = this.isInEditMode;
                    $(this.expanderWrapper)[0].disabled = this.isDisabledOrInEditMode();

                    $(this.expanderWrapper).find('.ni-details-box').each(function() {
                        $(this).find('.ni-button').each(function() {
                            this.disabled = that.isDisabledOrInEditMode();
                        });
                        $(this).find('.ni-selector').each(function() {
                            this.disabled = that.isDisabledOrInEditMode();
                        });
                        $(this).find('.ni-colorbox-selector').each(function() {
                            this.disabled = that.isDisabledOrInEditMode();
                        });
                        $(this).find('.ni-hover-box').each(function() {
                            this.disabled = that.isDisabledOrInEditMode();
                        });
                    });
                    break;
                case 'tooltips':
                    $(this).find('.ni-plot-title').each(function(index, span) {
                        span.title = this.tooltips ? this.helpers.getPlotName(index) : '';
                    }.bind(this));
                    break;
                default:
                    break;
            }
        };
    };

    JQX('ni-plot-legend', PlotLegend);
}());
